import React, { FC, memo, useState } from 'react';
import {
  TariffIntegrationLimitType,
  TariffToolLimitsType,
  Tariff,
  TariffAiLimitType,
} from '../tariff.types';
import cls from './SetTariffLimitsTab.module.scss';
import { useNavigate } from 'react-router-dom';
import TariffSetConnectionLimits from '../TariffSetConnectionLimits';
import TariffSetAiLimit from '../TariffSetAiLimit/TariffSetAiLimit';
import TariffSetToolsLimit from '../TariffSetToolsLimit';

type SetTariffLimitsTabProps = {
  integrations: TariffIntegrationLimitType[];
  tools: TariffToolLimitsType[];
  aiLimits: TariffAiLimitType[];
  setToolsLimits: React.Dispatch<React.SetStateAction<TariffToolLimitsType[]>>;
  setIntegrationsLimits: React.Dispatch<React.SetStateAction<TariffIntegrationLimitType[]>>;
  setAiLimits: React.Dispatch<React.SetStateAction<TariffAiLimitType[]>>;
  updateLimitsHandler: () => Promise<void>;
  isNew: boolean;
  tariffData?: Tariff;
  onGeneralLimitsChange?: (generalLimits: {
    generalIntegrationsLimit?: number;
    generalAiLimit?: number;
  }) => void;
};

const SetTariffLimitsTab: FC<SetTariffLimitsTabProps> = memo((props) => {
  const {
    integrations,
    tools,
    aiLimits,
    setToolsLimits,
    setIntegrationsLimits,
    setAiLimits,
    updateLimitsHandler,
    isNew,
    tariffData,
    onGeneralLimitsChange,
  } = props;

  const navigate = useNavigate();

  const [pendingGeneralLimits, setPendingGeneralLimits] = useState<{
    generalIntegrationsLimit?: number;
    generalAiLimit?: number;
  }>({});

  const handleGeneralLimitsChange = (newLimits: {
    generalIntegrationsLimit?: number;
    generalAiLimit?: number;
  }) => {
    setPendingGeneralLimits((prev) => ({
      ...prev,
      ...newLimits,
    }));
  };

  const handleSaveAll = async () => {
    try {
      if (Object.keys(pendingGeneralLimits).length > 0 && onGeneralLimitsChange) {
        await onGeneralLimitsChange(pendingGeneralLimits);
        setPendingGeneralLimits({});
      }
      await updateLimitsHandler();
    } catch (error) {
      console.error('Error saving limits:', error);
    }
  };

  return (
    <div className={cls.LimitsContainer}>
      <div className="d-flex flex-column gap-3">
        {/* Integrations table */}
        <TariffSetConnectionLimits
          integrations={integrations}
          setIntegrationsLimits={setIntegrationsLimits}
          generalIntegrationsLimit={tariffData?.generalIntegrationsLimit}
          onGeneralLimitChange={(value: number) =>
            handleGeneralLimitsChange({
              generalIntegrationsLimit: value,
            })
          }
          isEditingGeneralLimit={true}
        />

        <TariffSetAiLimit
          aiLimits={aiLimits}
          setAiLimits={setAiLimits}
          generalAiLimit={tariffData?.generalAiLimit}
          onGeneralLimitChange={(value: number) =>
            handleGeneralLimitsChange({
              generalAiLimit: value,
            })
          }
          isEditingGeneralLimit={true}
        />

        <TariffSetToolsLimit tools={tools} setToolsLimits={setToolsLimits} />
      </div>

      <div className="d-flex gap-3">
        <button className={cls.ActionButton} type="button" onClick={handleSaveAll}>
          {isNew ? 'Create' : 'Save Changes'}
        </button>
        <button type="button" onClick={() => navigate(-1)} className={cls.RevokeButton}>
          Cancel
        </button>
      </div>
    </div>
  );
});

export default SetTariffLimitsTab;
